/**
 * Module summary:
 * This component renders one assessment question, its answer control, optional
 * comment field, and optional photo attachments. It also handles client-side
 * image validation and compression before photos are stored with a response.
 */

import React, { useRef } from 'react';
import { Question, QuestionPhoto } from '../../../types/assessment';
import { INPUT_LIMITS, sanitizeMultilineDraft } from '../../../utils/inputSecurity';
import './QuestionCard.css';

const MAX_PHOTOS = 4;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_LONG_EDGE = 1600;
const JPEG_QUALITY = 0.78;

export interface PhotoAttachment extends QuestionPhoto {}

interface QuestionCardProps {
  question: Question;
  value: any;
  comment: string;
  onChange: (value: any) => void;
  onCommentChange: (comment: string) => void;
  error?: string;
  searchQuery?: string;
  photos?: PhotoAttachment[];
  onPhotosChange?: (photos: PhotoAttachment[]) => void;
}

function highlightText(text: string, query: string): React.ReactNode {
  if (!query || !query.trim()) return text;
  // Escape the search term before building the regex so special characters are treated as text, not regex commands.
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i} className="question-card__highlight">{part}</mark> : part
  );
}

function cleanHelpText(text: string): string {
  return text
    .replace(/^Section:\s*/i, '')
    .replace(/^\s*\d+\.\s*/, '')
    .replace(/\s*\([^)]*\)\s*$/u, '')
    .trim();
}

function safeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    // Prefer the browser UUID helper so photo ids stay unique without custom bookkeeping.
    return crypto.randomUUID();
  }
  return `ph-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    // Create a temporary object URL so the browser can decode the selected file as an image.
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      // Revoke the temporary URL after decoding so the browser does not keep unnecessary blob references alive.
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to decode image: ${file.name}`));
    };
    img.src = url;
  });
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
}

async function compressImageFile(file: File): Promise<{ dataUrl: string; width?: number; height?: number }> {
  if (!file.type.startsWith('image/')) {
    // Skip canvas compression for non-image files and just convert them directly to a data URL.
    return { dataUrl: await fileToDataUrl(file) };
  }

  const img = await loadImageFromFile(file);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  const largest = Math.max(width, height);
  const scale = largest > MAX_LONG_EDGE ? MAX_LONG_EDGE / largest : 1;
  // Scale the long edge down when needed so mobile uploads remain crisp without becoming excessively large.
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    // Fall back to the original file data when the canvas context is unavailable.
    return { dataUrl: await fileToDataUrl(file), width, height };
  }

  ctx.drawImage(img, 0, 0, targetW, targetH);
  return {
    dataUrl: canvas.toDataURL('image/jpeg', JPEG_QUALITY),
    width: targetW,
    height: targetH,
  };
}

/**
 * Molecule: QuestionCard
 * Renders a single question with its input type and optional comment
 */
export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  value,
  comment,
  onChange,
  onCommentChange,
  error,
  searchQuery,
  photos = [],
  onPhotosChange
}) => {
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, source: 'upload' | 'camera') => {
    if (!e.target.files || !onPhotosChange) return;
    // Limit the imported files to the remaining available slots so the card never exceeds the photo cap.
    const remaining = MAX_PHOTOS - photos.length;
    const files = Array.from(e.target.files).slice(0, remaining);

    const rejected: string[] = [];
    const validFiles: File[] = [];
    files.forEach((file) => {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        // Reject unsupported formats before compression so users get immediate feedback.
        rejected.push(`${file.name}: unsupported type`);
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        // Reject oversized files early to avoid wasting time compressing uploads that still violate the limit.
        rejected.push(`${file.name}: exceeds 5 MB`);
      } else {
        validFiles.push(file);
      }
    });

    if (rejected.length > 0) {
      alert(`Some files were not added:\n${rejected.join('\n')}`);
    }

    const newPhotos = await Promise.all(
      validFiles.map(async (file): Promise<PhotoAttachment> => {
        const compressed = await compressImageFile(file);
        return {
          // Store a generated id and metadata now so later upload and remove actions can target this exact photo.
          id: safeId(),
          questionId: question.id,
          name: file.name,
          mimeType: source === 'camera' ? 'image/jpeg' : file.type || 'image/jpeg',
          sizeBytes: file.size,
          createdAt: new Date().toISOString(),
          dataUrl: compressed.dataUrl,
        };
      })
    );

    if (newPhotos.length > 0) {
      onPhotosChange([...photos, ...newPhotos]);
    }

    e.target.value = '';
  };

  const removePhoto = (index: number) => {
    if (!onPhotosChange) return;
    // Remove the targeted photo by index while preserving every other attachment in order.
    onPhotosChange(photos.filter((_, i) => i !== index));
  };

  const renderInput = () => {
    switch (question.type) {
      case 'boolean':
        // Render paired yes/no controls for binary inspection questions.
        return (
          <div className="question-card__boolean">
            <label className={`boolean-option ${value === true ? 'boolean-option--selected' : ''}`}>
              <input
                type="radio"
                name={question.id}
                checked={value === true}
                onChange={() => onChange(true)}
              />
              <span className="boolean-option__text">Yes</span>
            </label>
            <label className={`boolean-option ${value === false ? 'boolean-option--selected' : ''}`}>
              <input
                type="radio"
                name={question.id}
                checked={value === false}
                onChange={() => onChange(false)}
              />
              <span className="boolean-option__text">No</span>
            </label>
          </div>
        );

      case 'scale':
        // Render fixed-number buttons when the question expects a rating scale.
        return (
          <div className="question-card__scale">
            {[1, 2, 3, 4, 5].map((num) => (
              <button
                key={num}
                type="button"
                className={`scale-button ${value === num ? 'scale-button--selected' : ''}`}
                onClick={() => onChange(num)}
              >
                {num}
              </button>
            ))}
          </div>
        );

      case 'text':
        // Render a multi-line text box for narrative answers.
        return (
          <textarea
            className="question-card__textarea"
            value={value || ''}
            onChange={(e) => onChange(sanitizeMultilineDraft(e.target.value, INPUT_LIMITS.questionText))}
            placeholder="Enter your answer..."
            rows={4}
          />
        );

      default:
        // Fall back to a single-line text input for any question type without a dedicated custom control.
        return (
          <input
            type="text"
            className="question-card__input"
            value={value || ''}
            onChange={(e) => onChange(sanitizeMultilineDraft(e.target.value, INPUT_LIMITS.questionText))}
            placeholder="Enter your answer..."
          />
        );
    }
  };

  return (
    <div className={`question-card ${error ? 'question-card--error' : ''}`}>
      <div className="question-card__header">
        <h3 className="question-card__text">
          {highlightText(question.text, searchQuery || '')}
          {question.required && <span className="question-card__required">*</span>}
        </h3>
        {question.helpText && (
          <p className="question-card__help">{cleanHelpText(question.helpText)}</p>
        )}
      </div>

      <div className="question-card__input-wrapper">
        {renderInput()}
      </div>

      {question.hasCommentField && (
        <div className="question-card__comment">
          <label className="question-card__comment-label">
            {question.commentPrompt}
          </label>
          <textarea
            className="question-card__comment-input"
            value={comment}
            onChange={(e) => onCommentChange(sanitizeMultilineDraft(e.target.value, INPUT_LIMITS.questionComment))}
            rows={2}
            placeholder="Add additional details (optional)..."
          />
        </div>
      )}

      {onPhotosChange && (
        <div className="question-card__photos">
          <div className="question-card__photos-header">
            <span className="question-card__photos-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              Photos ({photos.length}/{MAX_PHOTOS})
            </span>
            {photos.length < MAX_PHOTOS && (
              <div className="question-card__photos-actions">
                <button type="button" className="question-card__photos-add" onClick={() => cameraInputRef.current?.click()}>
                  Take Photo
                </button>
                <button type="button" className="question-card__photos-add question-card__photos-add--secondary" onClick={() => uploadInputRef.current?.click()}>
                  Upload File
                </button>
              </div>
            )}
            <input
              ref={uploadInputRef}
              type="file"
              multiple
              accept={ACCEPTED_TYPES.join(',')}
              onChange={(e) => { void handleFileSelect(e, 'upload'); }}
              className="question-card__photos-input"
              aria-label="Upload photo"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              capture="environment"
              onChange={(e) => { void handleFileSelect(e, 'camera'); }}
              className="question-card__photos-input"
              aria-label="Take photo"
            />
          </div>
          {photos.length > 0 && (
            <div className="question-card__photos-grid">
              {photos.map((photo, i) => (
                <div key={photo.id || i} className="question-card__photo-thumb">
                  <img src={photo.dataUrl || photo.storageUrl} alt={photo.name} />
                  <button type="button" className="question-card__photo-remove" onClick={() => removePhoto(i)} aria-label={`Remove ${photo.name}`}>X</button>
                  <span className="question-card__photo-name">{photo.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {error && (
        <span className="question-card__error">{error}</span>
      )}
    </div>
  );
};

export default QuestionCard;
